import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { money, fmtDate } from "@/lib/constants";
import { useCamp } from "@/context/SettingsContext";
import PrintLogo from "@/components/print/PrintLogo";
import { Button } from "@/components/ui/button";
import DemoStamp from "@/components/DemoStamp";

export default function PrintCashDiff() {
  const { t } = useLang();
  const { camp } = useCamp();
  const [params] = useSearchParams();
  const date = params.get("date");
  const withHistory = params.get("history") === "1";
  const [rep, setRep] = useState(null);
  const [hist, setHist] = useState(null);

  useEffect(() => { api.get(`/cash/day?date=${date}`).then(({ data }) => setRep(data)).catch(() => {}); }, [date]);
  useEffect(() => {
    if (!withHistory) { setHist({ sellers: [] }); return; }
    api.get(`/cash/seller-history?date=${date}`).then(({ data }) => setHist(data)).catch(() => setHist({ sellers: [] }));
  }, [date, withHistory]);
  useEffect(() => {
    if (!rep || !hist) return;
    const timer = setTimeout(() => window.print(), 500);
    return () => clearTimeout(timer);
  }, [rep, hist]);

  if (!rep || !hist) return <div className="p-8 text-sm text-muted-foreground">…</div>;
  const c = rep.closure || {};
  const cashSales = (rep.sales || []).filter((s) => s.payment === "cash");
  const paidRows = rep.account_paid_rows || [];

  return (
    <div className="print-page bg-white text-slate-900 mx-auto max-w-[520px] p-8 print:p-0" data-testid="print-cashdiff">
      <DemoStamp />
      <div className="flex justify-end print:hidden mb-3">
        <Button size="sm" onClick={() => window.print()} data-testid="print-cashdiff-now"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
      </div>
      <div className="flex items-center gap-3">
        <PrintLogo camp={camp} testid="print-cashdiff-logo" />
        <div>
          <h1 className="font-display font-extrabold text-xl">{camp.camp_name}</h1>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("cash_diff_list")} · {rep.date}</p>
        </div>
      </div>

      <div className="mt-5 flex justify-between items-baseline border-b-2 border-slate-800 pb-2">
        <span className="font-bold">{t("cash_difference")}</span>
        <span className={`font-display font-extrabold text-2xl tabular-nums ${c.difference ? "text-rose-600" : "text-emerald-600"}`} data-testid="print-cashdiff-amount">
          {c.difference == null ? "—" : money(c.difference)}
        </span>
      </div>
      <div className="mt-2 flex justify-between text-xs text-slate-500">
        <span>{t("cash_expected")}: {money(rep.cash_expected)}</span>
        <span>{t("cash_counted")}: {c.counted_cash == null ? "—" : money(c.counted_cash)}</span>
      </div>

      <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cash_sellers_today")}</p>
      <table className="mt-2 w-full text-xs" data-testid="print-cashdiff-sellers">
        <thead><tr className="text-left text-slate-500 border-b border-slate-300">
          <th>{t("seller")}</th><th className="text-right">{t("pay_cash")}</th>
          <th className="text-right">{t("cash_card_total")}</th><th className="text-right">{t("pay_account")}</th><th className="text-right">#</th>
        </tr></thead>
        <tbody>
          {(rep.sellers || []).map((s) => (
            <tr key={`${s.seller_name}-${s.seller_number}`} className="border-b border-slate-100">
              <td>{s.seller_name}{s.seller_number ? ` · ${s.seller_number}` : ""}</td>
              <td className="text-right tabular-nums">{money(s.cash)}</td>
              <td className="text-right tabular-nums">{money(s.card)}</td>
              <td className="text-right tabular-nums">{money(s.account)}</td>
              <td className="text-right tabular-nums">{s.count}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {cashSales.length > 0 && (
        <>
          <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("pay_cash")} — {t("sales")}</p>
          <table className="mt-2 w-full text-xs" data-testid="print-cashdiff-cashsales">
            <thead><tr className="text-left text-slate-500 border-b border-slate-300">
              <th>{t("invoice_number")}</th><th>{t("seller")}</th><th className="text-right">{t("total")}</th>
            </tr></thead>
            <tbody>
              {cashSales.map((s) => (
                <tr key={s.id} className="border-b border-slate-100">
                  <td className="tabular-nums">{s.number || "—"}</td>
                  <td>{s.seller_name || s.user || "—"}</td>
                  <td className="text-right tabular-nums">{money(s.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {paidRows.filter((r) => r.cash > 0).length > 0 && (
        <>
          <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cash_account_paid")}</p>
          <table className="mt-2 w-full text-xs" data-testid="print-cashdiff-paid">
            <thead><tr className="text-left text-slate-500 border-b border-slate-300">
              <th>{t("invoice_number")}</th><th>{t("bill_to")}</th><th>{t("seller")}</th><th className="text-right">{t("pay_cash")}</th>
            </tr></thead>
            <tbody>
              {paidRows.filter((r) => r.cash > 0).map((r, i) => (
                <tr key={`p${i}`} className="border-b border-slate-100">
                  <td className="tabular-nums">{r.number || "—"}</td>
                  <td>{r.guest_name || "—"}</td>
                  <td>{r.seller || r.by || "—"}</td>
                  <td className="text-right tabular-nums">{money(r.cash)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <p className="mt-8 text-[11px] text-slate-500">
        {c.closed_at ? `${fmtDate(c.closed_at)} · ${c.closed_by || ""}` : t("cash_not_closed")}
      </p>

      {withHistory && (hist.sellers || []).some((s) => s.count > 0) && (
        <div className="mt-6" data-testid="print-cashdiff-history">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{t("cash_diff_history")} · ≤ {hist.days}d{hist.limit ? ` · ⚠ ≥ ${money(hist.limit)}` : ""}</p>
          {(hist.sellers || []).filter((s) => s.count > 0).map((s) => (
            <div key={`${s.seller_name}-${s.seller_number}`} data-testid={`print-cashdiff-history-${s.seller_number || s.seller_name}`}
              className={`mt-2 ${s.over_limit ? "rounded border border-rose-400 bg-rose-50 p-2" : ""}`}>
              <p className="text-xs font-semibold flex justify-between">
                <span className={s.over_limit ? "text-rose-700" : ""}>
                  {s.over_limit && <span className="mr-1" data-testid="print-cashdiff-flag">⚠</span>}
                  {s.seller_name}{s.seller_number ? ` · ${s.seller_number}` : ""}
                </span>
                <span className={`tabular-nums ${s.over_limit ? "text-rose-700 font-bold" : ""}`}>{s.count}× · {money(s.total)}</span>
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {s.days.map((d) => (
                  <span key={d.date} className="rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] tabular-nums text-rose-700">
                    {d.date.slice(5)} · {money(d.difference)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 border-t border-slate-300 pt-6 text-[11px] text-slate-500">{t("cash_signature")}</div>
    </div>
  );
}
